import React, { useCallback, useRef, useState } from "react";
import ClearIcon from "@mui/icons-material/Clear";
import { Controller, useWatch, useFormContext } from "react-hook-form";
import dayjs, { Dayjs } from "dayjs";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  IconButton,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { NumericFormat } from "react-number-format";
import {
  IAddressModel,
  ILookupItemModel,
  ILookupItemSimpleModel,
  ReferrerTypeEnumModel,
} from "../../types/types";
import SingleValueAutoCompleteCategory from "../../ui-components/SingleValueAutoCompleteCategory";
import MultiValueAutoCompleteCategory from "../../ui-components/MultiValueAutoCompleteCategory";

import AddressInput from "../GoogleLocation/AddressInput";
import InputAdornment from "@mui/material/InputAdornment";
import { RootState } from "../../store/reducers";
import { useSelector } from "react-redux";
import ClientAutoComplete from "../AutoComplete/ClientAutoComplete";
import ReferrarAutoComplete from "../AutoComplete/ReferrarAutoComplete";
import {
  TRANSACTION_STATUS_OPTIONS,
  TransactionStatus,
  closingDateLabel,
  isPipelineStatus,
  usePipelineEnabled,
} from "../../hooks/usePipeline";

declare global {
  interface Window {
    initMap?: () => void;
  }
}

interface IProps {
  year: number;
  readonly?: boolean;
  isCreate?: boolean;
  /** Creating from the Pipeline folder (no folder year to stay within) */
  isPipelineFolder?: boolean;
}

export default function BasicTransaction(props: IProps) {
  const {
    year,
    readonly = false,
    isCreate = false,
    isPipelineFolder = false,
  } = props;
  const {
    control,
    setValue,
    getValues,
    clearErrors,
    trigger,
    getFieldState,
    formState,
  } = useFormContext();
  const pipelineEnabled = usePipelineEnabled();
  // Without the Pipeline feature the Lead Received Date field is hidden, so the
  // Closing Date field carries the "lead date after closing date" check instead
  const pipelineEnabledRef = useRef(pipelineEnabled);
  pipelineEnabledRef.current = pipelineEnabled;
  const closingDate = useWatch({ control, name: "date" });

  const status = (useWatch({ control, name: "status" }) ??
    TransactionStatus.Transaction) as TransactionStatus;
  const isPipelineEntry = isPipelineStatus(status);
  // Validators read refs so they always see the current Status
  const statusRef = useRef(status);
  statusRef.current = status;
  // New Transactions created inside a year folder must close in that year
  const restrictToYear = isCreate && !isPipelineFolder && !isPipelineEntry;
  const restrictToYearRef = useRef(restrictToYear);
  restrictToYearRef.current = restrictToYear;
  const initialStatus = formState.defaultValues?.status as
    | TransactionStatus
    | undefined;
  const [pendingStatus, setPendingStatus] = useState<TransactionStatus | null>(
    null,
  );

  const applyStatus = (newStatus: TransactionStatus) => {
    setValue("status", newStatus, { shouldDirty: true });
    const leadReceivedDate = getValues("leadReceivedDate");
    if (isPipelineStatus(newStatus)) {
      if (!leadReceivedDate) {
        setValue("leadReceivedDate", dayjs().startOf("day"), {
          shouldDirty: true,
        });
      }
      clearErrors(["address", "date"]);
    } else if (
      isCreate &&
      leadReceivedDate &&
      dayjs(leadReceivedDate).isSame(dayjs(), "day")
    ) {
      // Untouched default: a new Transaction starts with a blank Lead Received Date
      setValue("leadReceivedDate", null, { shouldDirty: true });
    }
    // The lead date message names the closing date per status; refresh a shown error
    if (getFieldState("leadReceivedDate").error) {
      trigger("leadReceivedDate");
    }
  };

  const handleStatusChange = (newStatus: TransactionStatus) => {
    // Moving a saved Transaction back to the Pipeline drops it from all analytics: confirm first
    if (
      !isCreate &&
      initialStatus === TransactionStatus.Transaction &&
      !isPipelineEntry &&
      isPipelineStatus(newStatus)
    ) {
      setPendingStatus(newStatus);
      return;
    }
    // Moving a saved Lead/Active entry out of the Pipeline into a year folder: confirm first
    if (
      !isCreate &&
      isPipelineStatus(initialStatus) &&
      isPipelineEntry &&
      newStatus === TransactionStatus.Transaction
    ) {
      setPendingStatus(newStatus);
      return;
    }
    applyStatus(newStatus);
  };
  const isMovingToTransactions =
    pendingStatus === TransactionStatus.Transaction;

  const { data: userData } = useSelector(
    (state: RootState) => state.commonData.userData,
  );

  const { hiddenFields } = useSelector(
    (state: RootState) => state.hiddenFields,
  );
  const selectedSource = useWatch({
    control,
    name: "source",
  }) as number | undefined;

  const selectedType = useWatch({
    control,
    name: "transactionType",
  }) as number | undefined;

  const selectedReason = useWatch({
    control,
    name: "reason",
  }) as number | undefined;

  const isDifferentYear = (date: Dayjs) => date.get("year") !== year;
  const isMonthlyRent =
    (selectedType &&
      userData?.transactionTypes?.find((x) => x.id === selectedType)
        ?.isMonthlyRent) ??
    false;

  const showReferralOut =
    (selectedType &&
      userData?.transactionTypes?.find((x) => x.id === selectedType)
        ?.showReferralOut) ??
    false;

  const isGoogleModeRef = useRef(true); // Track mode synchronously

  // Update ref when mode changes
  const handleModeChange = useCallback((newMode: boolean) => {
    isGoogleModeRef.current = newMode;
  }, []);

  return (
    <Grid container spacing={3}>
      {pipelineEnabled && (
        <>
          <Grid item xs={12} md={6}>
            <Controller
              name="status"
              control={control}
              defaultValue={TransactionStatus.Transaction}
              rules={{ required: "Please select a status" }}
              render={({ field }) => (
                <FormControl fullWidth>
                  <InputLabel shrink={true}>
                    Status <span style={{ color: "red" }}>*</span>
                  </InputLabel>
                  <Select
                    label="Status *"
                    notched={true}
                    readOnly={readonly}
                    value={field.value ?? TransactionStatus.Transaction}
                    onBlur={field.onBlur}
                    onChange={(e) =>
                      handleStatusChange(e.target.value as TransactionStatus)
                    }
                  >
                    {TRANSACTION_STATUS_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Controller
              name="leadReceivedDate"
              control={control}
              defaultValue={null}
              disabled={readonly}
              rules={{
                validate: (value) => {
                  if (!value) {
                    return isPipelineStatus(statusRef.current)
                      ? "Please enter the Lead Received Date"
                      : true;
                  }
                  const closingDate = getValues("date");
                  if (
                    closingDate &&
                    dayjs(value).isAfter(dayjs(closingDate), "day")
                  ) {
                    return `Lead Received Date can't be after the ${closingDateLabel(statusRef.current)}`;
                  }
                  return true;
                },
              }}
              render={({ field, fieldState: { error } }) => (
                <FormControl fullWidth>
                  <DatePicker
                    disabled={readonly}
                    {...field}
                    label={
                      <span>
                        Lead Received Date
                        {isPipelineEntry && (
                          <span style={{ color: "red" }}> *</span>
                        )}
                      </span>
                    }
                    value={field.value ? dayjs(field.value) : null}
                    onChange={(newValue) => field.onChange(newValue)}
                    slotProps={{
                      textField: {
                        onBlur: field.onBlur,
                        fullWidth: true,
                        error: !!error,
                        InputLabelProps: {
                          shrink: true,
                          sx: { backgroundColor: "background.paper", px: 1 },
                        },
                      },
                      field: { onBlur: () => field.onBlur(), clearable: true },
                      popper: { disablePortal: true },
                    }}
                  />
                  {error && (
                    <Typography variant="caption" color="error">
                      {error.message}
                    </Typography>
                  )}
                </FormControl>
              )}
            />
          </Grid>
        </>
      )}
      <Grid item xs={12} md={6}>
        <Controller
          name="transactionType"
          rules={{ required: "Please select a transaction type" }}
          control={control}
          defaultValue={""}
          render={({ field, fieldState: { error } }) => (
            <FormControl fullWidth>
              <InputLabel shrink={true}>
                Transaction Type <span style={{ color: "red" }}>*</span>
              </InputLabel>

              <Select
                {...field}
                label="Transaction Type *"
                // aria-invalid={control.name ? "true" : "false"}
                readOnly={readonly}
                notched={true}
                value={field.value ?? ""}
                onBlur={field.onBlur}
                displayEmpty={true}
                onChange={(e) => {
                  // debugger;
                  // const selected = transactionTypes.find(
                  //   (opt) => opt.id === e.target.value
                  // );
                  field.onChange(e.target.value ?? null);
                }}
              >
                {userData?.transactionTypes
                  ?.filter(
                    (x) =>
                      (isCreate && x.deleted !== true) ||
                      (isCreate === false &&
                        (selectedType === x.id || x.deleted !== true)),
                  )
                  .map((type) => (
                    <MenuItem key={type.id} value={type.id}>
                      {type.name}
                    </MenuItem>
                  ))}
              </Select>
              {error && (
                <Typography variant="caption" color="error">
                  Please select a transaction type
                </Typography>
              )}
            </FormControl>
          )}
        />
      </Grid>

      {/* {!hiddenFields.find((x) => x.name === "address") && ( */}
      <Grid item xs={12} md={6}>
        <Controller
          name="address"
          control={control}
          defaultValue={null}
          rules={{
            validate: (value: IAddressModel) => {
              if (
                !value ||
                !value.formattedAddress ||
                !value.formattedAddress.trim()
              ) {
                // Address is optional for Lead/Active entries
                return isPipelineStatus(statusRef.current)
                  ? true
                  : "Please enter an address";
              }

              // Use ref instead of state for immediate access
              const currentIsGoogleMode = isGoogleModeRef.current;

              // Check if it's a valid Google address by checking for coordinates and structured data
              const hasCoordinates =
                typeof value.latitude === "number" &&
                typeof value.longitude === "number";
              const hasStructuredData =
                (value.street && value.street.trim() !== "") ||
                (value.state && value.state.trim() !== "") ||
                (value.zip && value.zip.trim() !== "");

              // Valid scenarios:
              // 1. Full Google address (has both coordinates AND structured data)
              // 2. Free text (has formatted address but NO coordinates and NO structured data)
              const isValid = currentIsGoogleMode
                ? hasCoordinates && hasStructuredData
                : value?.formattedAddress?.trim().length > 1;

              if (isValid) {
                return true;
              }

              return currentIsGoogleMode
                ? "Please select a valid Google address from the suggestions"
                : "Please enter a valid address";
            },
          }}
          render={({ field, fieldState: { error } }) => (
            <FormControl fullWidth>
              <InputLabel shrink={true}>
                Address{" "}
                {!isPipelineEntry && <span style={{ color: "red" }}>*</span>}
              </InputLabel>
              <AddressInput
                value={field.value}
                onModeChange={handleModeChange}
                onChange={(value) => {
                  field.onChange(value);
                }}
              />
              {error && (
                <Typography variant="caption" color="error">
                  {error.message}
                </Typography>
              )}
            </FormControl>
          )}
        />
      </Grid>
      {/* )} */}
      <Grid item xs={12} md={6}>
        <Controller
          name="date"
          disabled={readonly}
          defaultValue={null}
          rules={{
            required: "Please select a date",
            // Re-check the Lead Received Date whenever the closing date changes
            deps: ["leadReceivedDate"],
            validate: (value) => {
              if (!value) return true;
              const leadReceivedDate = getValues("leadReceivedDate");
              if (
                !pipelineEnabledRef.current &&
                leadReceivedDate &&
                dayjs(leadReceivedDate).isAfter(dayjs(value), "day")
              ) {
                return `${closingDateLabel(statusRef.current)} can't be before the Lead Received Date (${dayjs(leadReceivedDate).format("MM/DD/YYYY")})`;
              }
              if (!restrictToYearRef.current) return true;
              const dateYear = dayjs(value).year();
              return dateYear === year || `Date must be in ${year}`;
            },
          }}
          control={control}
          render={({ field, fieldState: { error } }) => (
            <FormControl fullWidth>
              {/* <InputLabel shrink={true}>Date</InputLabel> */}
              <DatePicker
                disabled={readonly}
                shouldDisableYear={restrictToYear ? isDifferentYear : undefined}
                minDate={
                  restrictToYear ? dayjs(new Date(year, 0, 1)) : undefined
                }
                maxDate={
                  restrictToYear
                    ? dayjs(new Date(year + 1, 0, 1)).add(-1, "day")
                    : undefined
                }
                // openTo="year"
                // defaultMonth={dayjs(new Date(year, 0, 1))}
                {...field}
                label={
                  <span>
                    {closingDateLabel(status)}{" "}
                    <span style={{ color: "red" }}>*</span>
                  </span>
                }
                value={field.value ? dayjs(field.value) : null}
                onChange={(newValue) => field.onChange(newValue)}
                slotProps={{
                  textField: {
                    onBlur: field.onBlur,
                    fullWidth: true,
                    error: !!error,
                    // helperText: error?.message,
                    InputLabelProps: {
                      shrink: true,
                      sx: {
                        backgroundColor: "background.paper",
                        px: 1,
                        // "& .MuiOutlinedInput-notchedOutline": {
                        //   border: "none",
                        // },
                      },
                    },
                  },

                  field: { onBlur: () => field.onBlur(), clearable: true },
                  popper: {
                    // onClose: () => field.onBlur(),
                    disablePortal: true,
                  },
                }}
              />
              {error && error.type === "required" && (
                <Typography variant="caption" color="error">
                  Please select a date
                </Typography>
              )}
              {error && error.type === "validate" && (
                <Typography variant="caption" color="error">
                  {error.message}
                </Typography>
              )}
            </FormControl>
          )}
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <Controller
          name="price"
          control={control}
          rules={{
            required: `Please enter a ${
              isMonthlyRent ? "monthly Rent" : "price"
            }`,
          }}
          // defaultValue={null}
          defaultValue=""
          render={({ field, fieldState: { error } }) => (
            <FormControl fullWidth variant="filled">
              <NumericFormat
                fullWidth
                valueIsNumericString
                id="price"
                customInput={TextField}
                thousandSeparator
                onValueChange={(values) => field.onChange(values.floatValue)}
                allowLeadingZeros={false}
                decimalScale={0}
                onBlur={field.onBlur}
                // onChange={field.onChange}
                value={field.value ?? ""}
                label={
                  <span>
                    {isMonthlyRent ? "Monthly Rent" : "Price"}
                    <span style={{ color: "red" }}>*</span>
                  </span>
                }
                allowNegative={false}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">$</InputAdornment>
                    ),
                  },
                }}
                inputProps={{
                  maxLength: 50,
                }}
              />
              {/* <TextField
                disabled={readonly}
                id="price"
                type="number"
                error={!!error}
                // helperText={error?.message}
                label={
                  <span>
                    {isMonthlyRent ? "Monthly Rent" : "Price"}
                    <span style={{ color: "red" }}>*</span>
                  </span>
                }
                fullWidth
                onBlur={field.onBlur}
                onChange={field.onChange}
                value={field.value ?? ""}
                InputProps={{
                  inputComponent: NumericFormat as any,
                  inputProps: {
                    onBlur: field.onBlur,
                    onChange: field.onChange,
                    value: field.value ?? "",
                    displayType: "input",
                    thousandSeparator: ",",
                    decimalScale: 0,
                    prefix: "$",
                    allowNegative: false,
                  },
                }}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">$</InputAdornment>
                    ),
                  },
                }}
              /> */}
              {error && (
                <Typography variant="caption" color="error">
                  Please enter a {isMonthlyRent ? "monthly Rent" : "price"}
                </Typography>
              )}
            </FormControl>
          )}
        />
      </Grid>

      {!hiddenFields.find((x) => x.name === "source") && (
        <Grid item xs={12} md={6}>
          <Controller
            name="source"
            control={control}
            defaultValue={""}
            render={({ field }) => (
              <FormControl fullWidth>
                <InputLabel shrink={true}>Source</InputLabel>
                <Select
                  notched={true}
                  readOnly={readonly}
                  displayEmpty
                  {...field}
                  label="Source"
                  value={field.value ?? ""}
                  fullWidth
                  onChange={(e) => {
                    field.onChange(e.target.value ?? null);
                  }}
                  endAdornment={
                    field.value &&
                    !readonly && (
                      <IconButton
                        size="small"
                        sx={{ mr: 2 }}
                        onClick={(e) => {
                          field.onChange(null);
                        }}
                      >
                        <ClearIcon />
                      </IconButton>
                    )
                  }
                >
                  {userData?.sources
                    ?.filter((x) => x.name !== undefined || x.name !== "")
                    ?.sort((a, b) => a.name!.localeCompare(b.name!))
                    ?.filter(
                      (x) =>
                        (isCreate && x.deleted !== true) ||
                        (isCreate === false &&
                          (selectedSource === x.id || x.deleted !== true)),
                    )
                    ?.map((source) => (
                      <MenuItem key={source.id} value={source.id}>
                        {source.name}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            )}
          />
        </Grid>
      )}
      {selectedSource &&
        !hiddenFields.find((x) => x.name === "source") &&
        userData?.sources?.find((x) => x.id === selectedSource)
          ?.showVendorReferrer && (
          <Grid item xs={12} md={6}>
            <Controller
              name="vendorReferrers"
              defaultValue={[]}
              control={control}
              render={({ field }) => (
                <ReferrarAutoComplete
                  id="vendorReferrers"
                  referrerType={ReferrerTypeEnumModel.Vendor}
                  helperText={undefined}
                  error={undefined}
                  selected={field.value ?? []}
                  onChange={(event, value: ILookupItemSimpleModel[] | null) => {
                    field.onChange(value);
                  }}
                  label={"Vendor Referrer"}
                ></ReferrarAutoComplete>
              )}
            />
          </Grid>
        )}
      {selectedSource &&
        !hiddenFields.find((x) => x.name === "source") &&
        userData?.sources?.find((x) => x.id === selectedSource)
          ?.showSoiReferrer && (
          <Grid item xs={12} md={6}>
            <Controller
              name="soiReferrers"
              control={control}
              defaultValue={[]}
              render={({ field }) => (
                <ReferrarAutoComplete
                  id="soiReferrers"
                  referrerType={ReferrerTypeEnumModel.Soi}
                  helperText={undefined}
                  error={undefined}
                  selected={field.value ?? []}
                  onChange={(event, value: ILookupItemSimpleModel[] | null) => {
                    field.onChange(value);
                  }}
                  label={"SOI Referrer"}
                ></ReferrarAutoComplete>
              )}
            />
          </Grid>
        )}
      {selectedSource &&
        !hiddenFields.find((x) => x.name === "source") &&
        userData?.sources?.find((x) => x.id === selectedSource)
          ?.showAgentReferrer && (
          <Grid item xs={12} md={6}>
            <Controller
              name="agentReferrers"
              control={control}
              defaultValue={[]}
              render={({ field }) => (
                <ReferrarAutoComplete
                  id="agentReferrers"
                  referrerType={ReferrerTypeEnumModel.Agent}
                  helperText={undefined}
                  error={undefined}
                  selected={field.value ?? []}
                  onChange={(event, value: ILookupItemSimpleModel[] | null) => {
                    field.onChange(value);
                  }}
                  label={"Agent Referrer"}
                ></ReferrarAutoComplete>
              )}
            />
          </Grid>
        )}

      {showReferralOut && (
        <Grid item xs={12} md={6}>
          <Controller
            name="agentReferrered"
            control={control}
            defaultValue={[]}
            render={({ field }) => (
              <ReferrarAutoComplete
                id="agentReferrered"
                referrerType={ReferrerTypeEnumModel.ReferredOut}
                helperText={undefined}
                error={undefined}
                selected={field.value ?? []}
                onChange={(event, value: ILookupItemSimpleModel[] | null) => {
                  field.onChange(value);
                }}
                label={"Agent Referred"}
              ></ReferrarAutoComplete>
            )}
          />
        </Grid>
      )}

      {showReferralOut && (
        <Grid item xs={12} md={6}>
          <Controller
            name="referralOutTypeId"
            control={control}
            defaultValue={""}
            render={({ field }) => (
              <FormControl fullWidth>
                <InputLabel shrink={true}>Referral Out Type</InputLabel>
                <Select
                  displayEmpty
                  {...field}
                  notched={true}
                  readOnly={readonly}
                  label="Referral Out Type"
                  value={field.value ?? ""}
                  onChange={(e) => {
                    field.onChange(e.target.value ?? null);
                  }}
                  endAdornment={
                    field.value &&
                    !readonly && (
                      <IconButton
                        size="small"
                        sx={{ mr: 2 }}
                        onClick={(e) => {
                          field.onChange(null);
                        }}
                      >
                        <ClearIcon />
                      </IconButton>
                    )
                  }
                >
                  {userData?.referralOutTypes
                    ?.filter((x) => x.name !== undefined && x.name !== "")
                    ?.sort((a, b) => a.name!.localeCompare(b.name!))
                    ?.filter((x) => x.deleted !== true)
                    ?.map((type) => (
                      <MenuItem key={type.id} value={type.id}>
                        {type.name}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            )}
          />
        </Grid>
      )}

      <Grid item xs={12} md={6}>
        <Controller
          name="clients"
          control={control}
          defaultValue={[]}
          render={({ field }) => (
            <ClientAutoComplete
              helperText={undefined}
              error={undefined}
              onChange={(event, value: ILookupItemModel[] | null) => {
                field.onChange(value);
              }}
              id="clients"
              selected={field.value ?? []}
              label={"Clients"}
              placeholder="Search or add new clients"
            ></ClientAutoComplete>
          )}
        />
      </Grid>

      {hiddenFields?.find((x) => x.name === "reason") === undefined && (
        <Grid item xs={12} md={6}>
          <Controller
            name="reason"
            control={control}
            defaultValue={""}
            render={({ field }) => (
              <FormControl fullWidth>
                <InputLabel shrink={true}>Reason</InputLabel>
                <Select
                  displayEmpty
                  {...field}
                  notched={true}
                  readOnly={readonly}
                  label="Reason"
                  value={field.value ?? ""}
                  onChange={(e) => {
                    // const selected = reasonOptions.find(
                    //   (opt) => opt.id === e.target.value
                    // );
                    field.onChange(e.target.value ?? null);
                  }}
                  endAdornment={
                    field.value &&
                    !readonly && (
                      <IconButton
                        size="small"
                        sx={{ mr: 2 }}
                        onClick={(e) => {
                          field.onChange(null);
                        }}
                      >
                        <ClearIcon />
                      </IconButton>
                    )
                  }
                >
                  {userData?.reasons
                    ?.filter((x) => x.name !== undefined || x.name !== "")
                    ?.sort((a, b) => a.name!.localeCompare(b.name!))
                    ?.filter(
                      (x) =>
                        (isCreate && x.deleted !== true) ||
                        (isCreate === false &&
                          (selectedReason === x.id || x.deleted !== true)),
                    )
                    ?.map((reason) => (
                      <MenuItem key={reason.id} value={reason.id}>
                        {reason.name}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            )}
          />
        </Grid>
      )}

      {!hiddenFields.find((x) => x.name === "customArea") && (
        <Grid item xs={12} md={6}>
          <Controller
            name="area"
            defaultValue={""}
            control={control}
            render={({ field }) => (
              <SingleValueAutoCompleteCategory
                readOnly={readonly}
                freeSolo={true}
                id="area"
                options={userData?.areas ?? []}
                value={field.value ?? null}
                onChange={(event, value: ILookupItemModel | null) => {
                  field.onChange(value);
                }}
                helperText={undefined}
                placeholder="Select"
                error={undefined}
                label={"Custom Area"}
              />
            )}
          />
        </Grid>
      )}

      {!hiddenFields.find((x) => x.name === "coAgent") && (
        <Grid item xs={12} md={6}>
          <Controller
            name="coAgentMember"
            defaultValue={null}
            control={control}
            render={({ field }) => (
              <SingleValueAutoCompleteCategory
                readOnly={readonly}
                id="coAgentMember"
                options={userData?.coAgents ?? []}
                value={field.value ?? null}
                onChange={(event, value: ILookupItemModel | null) => {
                  field.onChange(value);
                }}
                helperText={undefined}
                error={undefined}
                label={"Team Member/Co-Agent"}
                placeholder={"Type or Select"}
              />
            )}
          />
        </Grid>
      )}

      {!hiddenFields.find((x) => x.name === "coAgent") && (
        <Grid item xs={12} md={6}>
          <Controller
            name="coAgentMember2"
            defaultValue={null}
            control={control}
            render={({ field }) => (
              <SingleValueAutoCompleteCategory
                readOnly={readonly}
                id="coAgentMember2"
                options={userData?.coAgents ?? []}
                value={field.value ?? null}
                onChange={(event, value: ILookupItemModel | null) => {
                  field.onChange(value);
                }}
                helperText={undefined}
                error={undefined}
                label={"Team Member/Co-Agent 2"}
                placeholder={"Type or Select"}
              />
            )}
          />
        </Grid>
      )}

      {!hiddenFields.find((x) => x.name === "specialGroups") && (
        <Grid item xs={12} md={6}>
          <Controller
            name="specialGroups"
            control={control}
            defaultValue={[]}
            render={({ field }) => (
              <MultiValueAutoCompleteCategory
                readOnly={readonly}
                options={userData?.specialGroups ?? []}
                id="specialGroups"
                freeSolo={false}
                value={field.value}
                onChange={(event, value: ILookupItemModel[] | null) => {
                  field.onChange(value);
                }}
                helperText={undefined}
                error={undefined}
                label={"Special Groups"}
                placeholder={"Select"}
              />
            )}
          />
        </Grid>
      )}
      {!hiddenFields.find((x) => x.name === "specialTags") && (
        <Grid item xs={12} md={6}>
          <Controller
            name="specialTags"
            defaultValue={[]}
            control={control}
            render={({ field }) => (
              <MultiValueAutoCompleteCategory
                options={userData?.specialTags ?? []}
                readOnly={readonly}
                id="specialTags"
                value={field.value ?? null}
                onChange={(event, value: ILookupItemModel[] | null) => {
                  field.onChange(value);
                }}
                helperText={undefined}
                error={undefined}
                label={"Special Tags"}
                freeSolo={false}
                placeholder={"Select"}
              />
            )}
          />
        </Grid>
      )}
      {!hiddenFields.find((x) => x.name === "vendors") && (
        <Grid item xs={12} md={6}>
          <Controller
            name="vendors"
            control={control}
            defaultValue={[]}
            render={({ field }) => (
              <ClientAutoComplete
                helperText={undefined}
                error={undefined}
                onChange={(event, value: ILookupItemSimpleModel[] | null) => {
                  field.onChange(value);
                }}
                id="vendors"
                selected={field.value ?? []}
                label={"Vendors"}
                placeholder="Search or add new vendors"
              />
            )}
          />
        </Grid>
      )}
      {!hiddenFields.find((x) => x.name === "notes") && (
        <Grid item xs={12}>
          <Controller
            name="notes"
            control={control}
            render={({ field }) => (
              <TextField
                disabled={readonly}
                {...field}
                label="Notes"
                value={field.value ?? ""}
                slotProps={{
                  inputLabel: {
                    shrink: true,
                  },
                  input: {
                    notched: true,
                  },
                }}
                multiline
                rows={4}
                fullWidth
              />
            )}
          />
        </Grid>
      )}
      {/* <Grid item xs={12}>
        <CustomAutocomplete options={mockTags.map((s) => s.name)} />
      </Grid> */}
      <Dialog open={pendingStatus !== null} onClose={() => setPendingStatus(null)}>
        <DialogTitle>
          {isMovingToTransactions ? "Move to Transactions?" : "Move to Pipeline?"}
        </DialogTitle>
        <DialogContent>
          {isMovingToTransactions ? (
            <DialogContentText>
              Changing the status to Transaction moves this entry from the
              Pipeline folder to{" "}
              {closingDate
                ? `the ${dayjs(closingDate).year()} folder (the year of its Closing Date)`
                : "the year folder of its Closing Date"}{" "}
              once saved. It will count in that year's summaries and analytics,
              and your brokerage anniversary financial defaults will be applied.
            </DialogContentText>
          ) : (
            <DialogContentText>
              Changing the status to{" "}
              {TRANSACTION_STATUS_OPTIONS.find((o) => o.value === pendingStatus)
                ?.label ?? ""}{" "}
              moves this entry to the Pipeline folder once saved. It will no
              longer count in its year folder, summaries or analytics until its
              status is set back to Transaction.
            </DialogContentText>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingStatus(null)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => {
              if (pendingStatus !== null) applyStatus(pendingStatus);
              setPendingStatus(null);
            }}
          >
            {isMovingToTransactions ? "Move to Transactions" : "Move to Pipeline"}
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
}
